import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  ArrowLeft,
  Pencil,
  Archive,
  AlertTriangle,
  Upload,
  FileText,
  Wrench,
  Sliders,
  ClipboardList,
  Calendar,
  Hash,
  Tag,
  ListChecks,
  History,
} from 'lucide-react'
import { fetchRisk, fetchRiskHistory, archiveRiskApi } from '@frontend/lib/api'
import { usePermissions } from '@frontend/hooks/usePermissions'
import { formatDate } from '@frontend/lib/utils'
import { AppLayout } from '@frontend/components/layout/AppLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@frontend/components/ui/card'
import { Button } from '@frontend/components/ui/button'
import { Spinner } from '@frontend/components/ui/spinner'
import { MetadataItem } from '@frontend/components/ui/metadata-item'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@frontend/components/ui/dialog'
import { RiskModal } from '@frontend/components/risks/RiskModal'
import { BulkUploadRisksDialog } from '@frontend/components/risks/BulkUploadRisksDialog'
import { EditHistoryTimeline } from '@frontend/components/history'

export function RiskDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { can } = usePermissions()
  const queryClient = useQueryClient()
  const [editOpen, setEditOpen] = useState(false)
  const [archiveOpen, setArchiveOpen] = useState(false)
  const [uploadOpen, setUploadOpen] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['risk', id],
    queryFn: () => fetchRisk(id!),
    enabled: !!id,
  })

  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['risk-history', id],
    queryFn: () => fetchRiskHistory(id!),
    enabled: !!id,
  })

  const archiveMutation = useMutation({
    mutationFn: () => archiveRiskApi(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['risk', id] })
      queryClient.invalidateQueries({ queryKey: ['risks'] })
      queryClient.invalidateQueries({ queryKey: ['risk-history', id] })
      setArchiveOpen(false)
      toast.success('Risk archived')
    },
    onError: () => toast.error('Failed to archive risk'),
  })

  const risk = data?.risk
  const isArchived = !!risk?.archivedAt

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      </AppLayout>
    )
  }

  if (error || !risk) {
    return (
      <AppLayout>
        <div className="py-16 text-center">
          <p className="text-destructive">Risk not found.</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/risks')}>
            Back to Risks
          </Button>
        </div>
      </AppLayout>
    )
  }

  const riskNamesMap = new Map([[risk.id, risk.name]])

  return (
    <AppLayout>
      <div className="space-y-6">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/risks')}
            className="mb-2 -ml-2 text-muted-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Risks
          </Button>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">{risk.name}</h1>
              {risk.archivedAt && (
                <p className="mt-1 text-sm text-muted-foreground">
                  Archived {formatDate(risk.archivedAt)}
                  {risk.archivedBy ? ` by ${risk.archivedBy}` : ''}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!isArchived && (
                <>
                  <Button onClick={() => setUploadOpen(true)}>
                    <Upload className="h-4 w-4 mr-1" />
                    Upload to Client
                  </Button>
                  {can('risks.edit') && (
                    <Button variant="outline" onClick={() => setEditOpen(true)}>
                      <Pencil className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                  )}
                  {can('risks.archive') && (
                    <Button variant="outline" onClick={() => setArchiveOpen(true)}>
                      <Archive className="h-4 w-4 mr-1" />
                      Archive
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <MetadataItem
              icon={FileText}
              label="Description"
              value={risk.description || <em className="text-muted-foreground">None</em>}
            />
            <MetadataItem icon={Wrench} label="Treatment Plan" value={risk.treatmentPlan} />
            <MetadataItem
              icon={ClipboardList}
              label="Treatment Details"
              value={risk.treatmentDetails || <em className="text-muted-foreground">None</em>}
            />
            <MetadataItem icon={Calendar} label="Created" value={formatDate(risk.createdAt)} />

            <MetadataItem icon={Sliders} label="Impact" value={`${risk.impact} / 5`} />
            <MetadataItem icon={Sliders} label="Likelihood" value={`${risk.likelihood} / 5`} />
            <MetadataItem icon={Hash} label="Score" value={`${risk.score} / 25`} />

            <MetadataItem icon={Sliders} label="Residual Impact" value={`${risk.residualImpact} / 5`} />
            <MetadataItem icon={Sliders} label="Residual Likelihood" value={`${risk.residualLikelihood} / 5`} />
            <MetadataItem icon={Hash} label="Residual Score" value={`${risk.residualScore} / 25`} />

            <MetadataItem
              icon={ListChecks}
              label="Controls"
              value={
                risk.controls.length > 0 ? (
                  <span className="text-sm">{risk.controls.join(', ')}</span>
                ) : (
                  <em className="text-muted-foreground">None</em>
                )
              }
            />
            <MetadataItem
              icon={Tag}
              label="Categories"
              value={
                risk.categories.length > 0 ? (
                  <span className="text-sm">{risk.categories.join(', ')}</span>
                ) : (
                  <em className="text-muted-foreground">None</em>
                )
              }
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <History className="h-4 w-4" />
              Edit History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {historyLoading ? (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            ) : (
              <EditHistoryTimeline
                edits={historyData?.edits ?? []}
                emptyMessage="No changes have been recorded for this risk yet."
              />
            )}
          </CardContent>
        </Card>
      </div>

      {!isArchived && (
        <>
          <RiskModal mode="edit" risk={risk} open={editOpen} onOpenChange={setEditOpen} />
          <BulkUploadRisksDialog
            riskIds={[risk.id]}
            riskNames={riskNamesMap}
            open={uploadOpen}
            onOpenChange={setUploadOpen}
          />
        </>
      )}

      <Dialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Archive Risk
            </DialogTitle>
            <DialogDescription>
              Archive "{risk.name}"? This hides it from the catalog but preserves the record. Archived risks cannot be
              edited or pushed to clients. The name becomes available for reuse.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveOpen(false)} disabled={archiveMutation.isPending}>
              Cancel
            </Button>
            <Button onClick={() => archiveMutation.mutate()} disabled={archiveMutation.isPending}>
              {archiveMutation.isPending ? 'Archiving...' : 'Archive'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  )
}
